import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { eur, fmtDate } from "@/lib/format";
import { LogIn, LogOut, Coffee, Clock, CalendarDays, Target, Users, CalendarRange, Plane, Plus, Wallet, CheckCircle2, Download, TrendingUp, Timer, FileText, XCircle, CircleDot, ListChecks } from "lucide-react";

const hm = (min) => `${Math.floor((min || 0) / 60)}h ${(min || 0) % 60}m`;
const iso = (d) => d.toISOString().slice(0, 10);
// Stili badge per lo stato leggibile dell'appuntamento (da effettuare / in corso / completato / annullato)
const APPT_STATUS_STYLE = {
  todo: { cls: "text-[hsl(var(--hi-status-info))] bg-[hsl(var(--hi-status-info)/0.12)] border-[hsl(var(--hi-status-info)/0.3)]", Icon: CircleDot },
  in_progress: { cls: "text-[hsl(var(--hi-gold))] bg-[hsl(var(--hi-gold)/0.12)] border-[hsl(var(--hi-gold)/0.35)]", Icon: Timer },
  completed: { cls: "text-[hsl(var(--hi-status-success))] bg-[hsl(var(--hi-status-success)/0.12)] border-[hsl(var(--hi-status-success)/0.3)]", Icon: CheckCircle2 },
  cancelled: { cls: "text-muted-foreground bg-muted border-border line-through", Icon: XCircle },
};
const fmtToday = (isoStr) => {
  try {
    const d = isoStr ? new Date(isoStr + "T00:00:00") : new Date();
    return d.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" });
  } catch { return ""; }
};
const LEAVE_TYPES = [
  { v: "ferie", l: "Ferie" }, { v: "permesso", l: "Permesso" },
  { v: "malattia", l: "Malattia" }, { v: "formazione", l: "Formazione" }, { v: "altro", l: "Altro" },
];
const LEAVE_LABEL = Object.fromEntries(LEAVE_TYPES.map((t) => [t.v, t.l]));
const LEAVE_STATUS = {
  pending: { l: "In attesa", c: "text-[hsl(var(--hi-status-info))] bg-[hsl(var(--hi-status-info)/0.12)]" },
  approved: { l: "Approvata", c: "text-[hsl(var(--hi-status-success))] bg-[hsl(var(--hi-status-success)/0.12)]" },
  rejected: { l: "Rifiutata", c: "text-destructive bg-destructive/10" },
};
const STATUS = {
  out: { label: "Non timbrato", tone: "text-muted-foreground" },
  in: { label: "Al lavoro", tone: "text-[hsl(var(--hi-status-success))]" },
  break: { label: "In pausa", tone: "text-[hsl(var(--hi-status-info))]" },
  done: { label: "Giornata conclusa", tone: "text-muted-foreground" },
};

export default function MyDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [weekShifts, setWeekShifts] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [leave, setLeave] = useState({ type: "ferie", start: "", end: "", note_staff: "" });
  const [payroll, setPayroll] = useState([]);
  const [prog, setProg] = useState(null);
  const [selectedAppt, setSelectedAppt] = useState(null);

  const load = useCallback(async () => {
    const today = new Date();
    const wk = new Date(); wk.setDate(wk.getDate() + 6);
    try {
      const [dash, sh, ab, pr, pg] = await Promise.all([
        api.get("/me/dashboard"),
        api.get(`/me/shifts?from=${iso(today)}&to=${iso(wk)}`),
        api.get("/me/absences"),
        api.get("/me/payroll"),
        api.get("/me/payroll/progress").catch(() => ({ data: null })),
      ]);
      setData(dash.data); setWeekShifts(sh.data); setAbsences(ab.data); setPayroll(pr.data); setProg(pg.data);
    } catch (e) { toast.error("Errore nel caricamento"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const downloadPayslip = async (pid) => {
    try {
      const res = await api.get(`/me/payroll/${pid}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error("PDF non disponibile"); }
  };

  const confirmPayroll = async (pid) => {
    try { await api.post(`/me/payroll/${pid}/confirm`); toast.success("Presa visione confermata"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const act = async (path, msg) => {
    setBusy(true);
    try { await api.post(`/attendance/${path}`); toast.success(msg); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const submitLeave = async () => {
    if (!leave.start || !leave.end) { toast.error("Inserisci le date"); return; }
    try {
      await api.post("/team/absences", {
        type: leave.type, start: leave.start + "T00:00:00+00:00",
        end: leave.end + "T23:59:59+00:00", full_day: true, note_staff: leave.note_staff,
      });
      toast.success("Richiesta inviata, in attesa di approvazione");
      setLeaveOpen(false); setLeave({ type: "ferie", start: "", end: "", note_staff: "" }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  if (!data) return <div className="space-y-4"><Skeleton className="h-40 w-full" /><Skeleton className="h-32 w-full" /></div>;

  const st = STATUS[data.clock_status] || STATUS.out;
  const firstName = (user?.name || "").split(" ")[0];
  const counts = data.counts || { total: 0, completed: 0, remaining: 0, cancelled: 0 };
  const shiftLabel = data.shift && data.shift.windows.length
    ? data.shift.windows.map((w) => `${w.start}–${w.end}`).join(" · ")
    : null;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-semibold">Ciao {firstName}</h1>
        <p className="text-sm text-muted-foreground mt-1">La tua giornata in Hair Identity</p>
      </div>

      {/* Riepilogo di oggi */}
      <Card className="p-5" data-testid="today-summary">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Riepilogo di oggi</div>
            <div className="font-heading text-xl font-semibold capitalize" data-testid="today-date">{fmtToday(data.date_iso)}</div>
            <div className="text-sm text-muted-foreground mt-1 flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5 text-[hsl(var(--hi-gold))]" />
              {shiftLabel ? <span>Orario previsto: <strong className="text-foreground">{shiftLabel}</strong>{data.shift?.minutes ? <span className="text-muted-foreground"> · {hm(data.shift.minutes)}</span> : null}</span>
                          : <span>Nessun turno programmato oggi</span>}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-lg border border-border px-4 py-2 text-center min-w-[84px]" data-testid="count-total">
              <div className="font-heading text-2xl font-semibold">{counts.total}</div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Appuntamenti</div>
            </div>
            <div className="rounded-lg border border-[hsl(var(--hi-status-success)/0.3)] bg-[hsl(var(--hi-status-success)/0.06)] px-4 py-2 text-center min-w-[84px]" data-testid="count-completed">
              <div className="font-heading text-2xl font-semibold text-[hsl(var(--hi-status-success))]">{counts.completed}</div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Completati</div>
            </div>
            <div className="rounded-lg border border-[hsl(var(--hi-status-info)/0.3)] bg-[hsl(var(--hi-status-info)/0.06)] px-4 py-2 text-center min-w-[84px]" data-testid="count-remaining">
              <div className="font-heading text-2xl font-semibold text-[hsl(var(--hi-status-info))]">{counts.remaining}</div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Da svolgere</div>
            </div>
          </div>
        </div>
      </Card>

      {/* Timbratura */}
      <Card className="p-5">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Presenza</div>
            <div className={`font-heading text-2xl font-semibold ${st.tone}`} data-testid="clock-status">{st.label}</div>
            <div className="text-sm text-muted-foreground mt-1">Ore lavorate questo mese: <strong>{hm(data.worked_minutes)}</strong></div>
          </div>
          <div className="flex flex-wrap gap-2">
            {data.clock_status === "out" && <Button size="lg" onClick={() => act("clock-in", "Entrata registrata")} disabled={busy} data-testid="btn-clock-in"><LogIn className="h-5 w-5 mr-2" /> Timbra entrata</Button>}
            {data.clock_status === "in" && <>
              <Button size="lg" variant="outline" onClick={() => act("break-start", "Pausa iniziata")} disabled={busy} data-testid="btn-break-start"><Coffee className="h-5 w-5 mr-2" /> Pausa</Button>
              <Button size="lg" onClick={() => act("clock-out", "Uscita registrata")} disabled={busy} data-testid="btn-clock-out"><LogOut className="h-5 w-5 mr-2" /> Timbra uscita</Button>
            </>}
            {data.clock_status === "break" && <Button size="lg" onClick={() => act("break-end", "Rientro dalla pausa")} disabled={busy} data-testid="btn-break-end"><LogIn className="h-5 w-5 mr-2" /> Riprendi</Button>}
            {data.clock_status === "done" && <Badge className="text-sm py-2 px-4 bg-muted text-muted-foreground">Ci vediamo domani!</Badge>}
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Turno oggi + prossimo appuntamento */}
        <div className="space-y-4">
          <Card className="p-4">
            <div className="flex items-center gap-2 text-sm font-medium mb-2"><Clock className="h-4 w-4 text-[hsl(var(--hi-gold))]" /> Turno di oggi</div>
            {data.shift ? (
              <div className="flex flex-wrap gap-2">{data.shift.windows.map((w, i) => <Badge key={i} variant="secondary" className="text-sm py-1">{w.start}–{w.end}</Badge>)}</div>
            ) : <p className="text-sm text-muted-foreground">Nessun turno programmato oggi.</p>}
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-2 text-sm font-medium mb-2"><CalendarDays className="h-4 w-4 text-[hsl(var(--hi-gold))]" /> Prossimo appuntamento</div>
            {data.next_appointment ? (
              <div data-testid="next-appointment" className="space-y-2">
                <div>
                  <div className="font-heading text-xl font-semibold">{data.next_appointment.time}{data.next_appointment.end_time ? <span className="text-muted-foreground text-base font-normal">–{data.next_appointment.end_time}</span> : null} · {data.next_appointment.client_name}</div>
                  <div className="text-sm text-muted-foreground">{(data.next_appointment.services || []).join(", ")}</div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {data.next_appointment.duration_min ? <Badge variant="secondary" className="text-xs gap-1"><Timer className="h-3 w-3" /> {data.next_appointment.duration_min} min</Badge> : null}
                  {(() => { const s = APPT_STATUS_STYLE[data.next_appointment.display_status] || APPT_STATUS_STYLE.todo; const I = s.Icon; return <Badge variant="outline" className={`text-[10px] gap-1 ${s.cls}`}><I className="h-3 w-3" /> {data.next_appointment.display_label}</Badge>; })()}
                </div>
                {data.next_appointment.notes ? (
                  <div className="flex items-start gap-1.5 rounded-md bg-muted/60 px-2.5 py-2 text-xs text-muted-foreground" data-testid="next-appt-notes">
                    <FileText className="h-3.5 w-3.5 mt-0.5 shrink-0 text-[hsl(var(--hi-gold))]" />
                    <span className="whitespace-pre-wrap">{data.next_appointment.notes}</span>
                  </div>
                ) : null}
              </div>
            ) : <p className="text-sm text-muted-foreground">Nessun altro appuntamento oggi.</p>}
          </Card>
        </div>

        {/* Appuntamenti di oggi */}
        <Card className="p-4">
          <div className="flex items-center gap-2 text-sm font-medium mb-3"><CalendarDays className="h-4 w-4 text-[hsl(var(--hi-gold))]" /> Appuntamenti di oggi ({counts.total})</div>
          <div className="space-y-1 max-h-80 overflow-y-auto hi-scroll">
            {data.today_appointments.length === 0 && <p className="text-sm text-muted-foreground">Giornata libera da appuntamenti.</p>}
            {data.today_appointments.map((a) => {
              const s = APPT_STATUS_STYLE[a.display_status] || APPT_STATUS_STYLE.todo;
              const I = s.Icon;
              const isCancelled = a.display_status === "cancelled";
              return (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => setSelectedAppt(a)}
                  className={`w-full text-left flex items-center gap-3 rounded-lg px-2 py-2 border border-transparent transition-colors hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--hi-gold)/0.5)] ${isCancelled ? "opacity-60" : ""}`}
                  data-testid={`appt-${a.id}`}
                >
                  <div className="flex flex-col items-center w-14 shrink-0">
                    <span className="font-mono text-sm font-medium">{a.time}</span>
                    {a.end_time ? <span className="font-mono text-[10px] text-muted-foreground">{a.end_time}</span> : null}
                  </div>
                  <div className="w-px self-stretch bg-border" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{a.client_name}</div>
                    <div className="text-xs text-muted-foreground truncate">{(a.services || []).join(", ") || "—"}</div>
                    <div className="flex items-center gap-2 mt-1">
                      {a.duration_min ? <span className="text-[10px] text-muted-foreground inline-flex items-center gap-0.5"><Timer className="h-3 w-3" /> {a.duration_min} min</span> : null}
                      {a.notes ? <span className="text-[10px] text-muted-foreground inline-flex items-center gap-0.5"><FileText className="h-3 w-3" /> Note</span> : null}
                    </div>
                  </div>
                  <Badge variant="outline" className={`text-[10px] gap-1 shrink-0 ${s.cls}`}><I className="h-3 w-3" /> {a.display_label}</Badge>
                </button>
              );
            })}
          </div>
        </Card>
      </div>

      {/* Turni settimana + Ferie/Permessi */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-2 text-sm font-medium mb-3"><CalendarRange className="h-4 w-4 text-[hsl(var(--hi-gold))]" /> I miei turni (prossimi 7 giorni)</div>
          {weekShifts.length === 0 ? <p className="text-sm text-muted-foreground">Nessun turno programmato nei prossimi 7 giorni.</p> : (
            <div className="space-y-1.5">
              {weekShifts.map((s) => (
                <div key={s.date} className="flex items-center gap-3 py-1 border-b border-border/50 last:border-0" data-testid={`myshift-${s.date}`}>
                  <div className="w-28 text-sm"><span className="font-medium">{s.weekday}</span> <span className="text-muted-foreground text-xs">{s.date.slice(5)}</span></div>
                  <div className="flex-1 flex flex-wrap gap-1.5">{s.windows.map((w, i) => <Badge key={i} variant="secondary" className="text-xs">{w.start}–{w.end}</Badge>)}</div>
                  <div className="text-xs text-muted-foreground">{hm(s.minutes)}</div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-sm font-medium"><Plane className="h-4 w-4 text-[hsl(var(--hi-gold))]" /> Ferie & Permessi</div>
            <Button size="sm" variant="outline" onClick={() => setLeaveOpen(true)} data-testid="leave-request-btn"><Plus className="h-4 w-4 mr-1" /> Richiedi</Button>
          </div>
          {absences.length === 0 ? <p className="text-sm text-muted-foreground">Nessuna richiesta. Usa "Richiedi" per ferie o permessi.</p> : (
            <div className="space-y-2 max-h-56 overflow-y-auto hi-scroll">
              {absences.map((a) => {
                const st = LEAVE_STATUS[a.status] || {};
                return (
                  <div key={a.id} className="flex items-center gap-2 text-sm" data-testid={`my-absence-${a.id}`}>
                    <div className="flex-1"><span className="font-medium">{LEAVE_LABEL[a.type] || a.type}</span> <span className="text-xs text-muted-foreground">{(a.start||"").slice(0,10)} → {(a.end||"").slice(0,10)}</span></div>
                    <Badge className={`text-[10px] ${st.c || ""}`}>{st.l || a.status}</Badge>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      {/* Obiettivi — mostrati solo se assegnati (nessun box vuoto) */}
      {(data.personal_goals.length > 0 || data.team_goals.length > 0) && (
        <div className={`grid grid-cols-1 gap-4 ${data.personal_goals.length > 0 && data.team_goals.length > 0 ? "lg:grid-cols-2" : ""}`}>
          {data.personal_goals.length > 0 && (
            <Card className="p-4" data-testid="personal-goals-card">
              <div className="flex items-center gap-2 text-sm font-medium mb-3"><Target className="h-4 w-4 text-[hsl(var(--hi-gold))]" /> I tuoi obiettivi</div>
              {data.personal_goals.map((g, i) => (
                <div key={i} className="mb-3 last:mb-0" data-testid={`personal-goal-${i}`}>
                  <div className="flex justify-between text-sm mb-1"><span>{g.title}</span><span className="font-medium">{g.progress}%</span></div>
                  <Progress value={g.progress} />
                </div>
              ))}
            </Card>
          )}
          {data.team_goals.length > 0 && (
            <Card className="p-4" data-testid="team-goals-card">
              <div className="flex items-center gap-2 text-sm font-medium mb-3"><Users className="h-4 w-4 text-[hsl(var(--hi-gold))]" /> Obiettivi del team</div>
              {data.team_goals.map((g, i) => (
                <div key={i} className="mb-3 last:mb-0" data-testid={`team-goal-${i}`}>
                  <div className="flex justify-between text-sm mb-1"><span>{g.title}</span><span className="font-medium">{g.progress}%</span></div>
                  <Progress value={g.progress} />
                </div>
              ))}
            </Card>
          )}
        </div>
      )}

      {/* La mia retribuzione (periodo corrente) */}
      {prog && (prog.has_target || prog.total_gross > 0) && (
        <Card className="p-5 mt-6" data-testid="my-payroll-progress">
          <div className="flex items-center gap-2 mb-4"><TrendingUp className="h-5 w-5 text-[hsl(var(--hi-gold))]" /><h3 className="font-heading text-xl font-semibold">La mia retribuzione · questo mese</h3></div>
          {prog.has_target && prog.revenue_target > 0 && (
            <div className="mb-4">
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="text-muted-foreground">Fatturato: <strong className="text-foreground">{eur(prog.revenue_total)}</strong></span>
                <span className="text-muted-foreground">Obiettivo: <strong className="text-foreground">{eur(prog.revenue_target)}</strong></span>
              </div>
              <Progress value={prog.target_progress || 0} className="h-2" data-testid="my-target-progress" />
              <div className="text-xs text-muted-foreground mt-1">{prog.target_progress != null ? `${prog.target_progress}% dell'obiettivo` : "—"}</div>
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div className="rounded-lg border border-border p-3">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Fatturato</div>
              <div className="font-heading text-lg font-semibold" data-testid="my-revenue">{eur(prog.revenue_total)}</div>
            </div>
            <div className="rounded-lg border border-border p-3">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Bonus / commissioni</div>
              <div className="font-heading text-lg font-semibold text-[hsl(var(--hi-gold))]" data-testid="my-bonus">{eur(prog.bonus_total)}</div>
            </div>
            <div className="rounded-lg border border-border p-3">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Compenso stimato</div>
              <div className="font-heading text-lg font-semibold" data-testid="my-estimated">{eur(prog.total_gross)}</div>
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground mt-3">Stima indicativa del periodo in corso ({prog.from} → {prog.to}). L'importo definitivo è quello del cedolino registrato.</p>
        </Card>
      )}

      {/* Le mie retribuzioni */}
      <Card className="p-5 mt-6" data-testid="my-payroll-card">
        <div className="flex items-center gap-2 mb-4"><Wallet className="h-5 w-5 text-[hsl(var(--hi-gold))]" /><h3 className="font-heading text-xl font-semibold">Le mie retribuzioni</h3></div>
        {payroll.length === 0 ? (
          <div className="text-sm text-muted-foreground py-4 text-center">Nessuna retribuzione registrata.</div>
        ) : (
          <div className="space-y-2">
            {payroll.map((p) => (
              <div key={p.id} className="flex items-center justify-between rounded-lg border border-border p-3 flex-wrap gap-2" data-testid={`my-payroll-${p.id}`}>
                <div>
                  <div className="font-medium">{fmtDate(p.from)} – {fmtDate(p.to)}</div>
                  <div className="text-xs text-muted-foreground">
                    Lordo {eur(p.total_calculated)}{p.manual_bonus ? ` · bonus ${eur(p.manual_bonus)}` : ""}{p.deductions ? ` · trattenute ${eur(p.deductions)}` : ""}{p.advances ? ` · anticipi ${eur(p.advances)}` : ""} · {p.method}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-heading text-lg font-semibold text-[hsl(var(--hi-gold))]">{eur(p.total_paid)}</span>
                  <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => downloadPayslip(p.id)} data-testid={`my-payroll-pdf-${p.id}`} title="Scarica cedolino PDF"><Download className="h-4 w-4" /></Button>
                  {p.viewed_at ? (
                    <span className="inline-flex items-center gap-1 text-xs text-[hsl(var(--hi-status-success))]" data-testid={`my-payroll-confirmed-${p.id}`}><CheckCircle2 className="h-4 w-4" /> Preso visione {fmtDate(p.viewed_at)}</span>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => confirmPayroll(p.id)} data-testid={`my-payroll-confirm-${p.id}`}>Confermo presa visione</Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Dettaglio appuntamento (informazioni consentite al collaboratore) */}
      <Dialog open={!!selectedAppt} onOpenChange={(o) => !o && setSelectedAppt(null)}>
        <DialogContent data-testid="appt-detail-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">Dettaglio appuntamento</DialogTitle></DialogHeader>
          {selectedAppt && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="font-heading text-lg font-semibold">{selectedAppt.time}{selectedAppt.end_time ? `–${selectedAppt.end_time}` : ""}</div>
                {(() => { const s = APPT_STATUS_STYLE[selectedAppt.display_status] || APPT_STATUS_STYLE.todo; const I = s.Icon; return <Badge variant="outline" className={`gap-1 ${s.cls}`}><I className="h-3.5 w-3.5" /> {selectedAppt.display_label}</Badge>; })()}
              </div>
              <div className="grid grid-cols-1 gap-2 text-sm">
                <div className="flex items-center gap-2"><Users className="h-4 w-4 text-muted-foreground" /> <span className="text-muted-foreground">Cliente:</span> <strong>{selectedAppt.client_name || "—"}</strong></div>
                <div className="flex items-start gap-2"><ListChecks className="h-4 w-4 text-muted-foreground mt-0.5" /> <span className="text-muted-foreground">Servizi:</span> <strong className="flex-1">{(selectedAppt.services || []).join(", ") || "—"}</strong></div>
                <div className="flex items-center gap-2"><Timer className="h-4 w-4 text-muted-foreground" /> <span className="text-muted-foreground">Durata:</span> <strong>{selectedAppt.duration_min ? `${selectedAppt.duration_min} min` : "—"}</strong></div>
              </div>
              <div className="rounded-md bg-muted/60 px-3 py-2.5">
                <div className="text-xs font-medium mb-1 flex items-center gap-1.5"><FileText className="h-3.5 w-3.5 text-[hsl(var(--hi-gold))]" /> Note</div>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap" data-testid="appt-detail-notes">{selectedAppt.notes || "Nessuna nota per questo appuntamento."}</p>
              </div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setSelectedAppt(null)} data-testid="appt-detail-close">Chiudi</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={leaveOpen} onOpenChange={setLeaveOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading text-xl">Richiedi ferie o permesso</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Tipo</Label>
              <Select value={leave.type} onValueChange={(v) => setLeave({ ...leave, type: v })}>
                <SelectTrigger data-testid="leave-type"><SelectValue /></SelectTrigger>
                <SelectContent>{LEAVE_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Dal</Label><Input type="date" value={leave.start} onChange={(e) => setLeave({ ...leave, start: e.target.value })} data-testid="leave-start" /></div>
              <div className="space-y-1.5"><Label>Al</Label><Input type="date" value={leave.end} onChange={(e) => setLeave({ ...leave, end: e.target.value })} data-testid="leave-end" /></div>
            </div>
            <div className="space-y-1.5"><Label>Note (opzionale)</Label><Input value={leave.note_staff} onChange={(e) => setLeave({ ...leave, note_staff: e.target.value })} placeholder="Motivo o dettagli" data-testid="leave-note" /></div>
            <p className="text-xs text-muted-foreground">La richiesta resterà "In attesa" fino all'approvazione dell'Admin.</p>
          </div>
          <DialogFooter><Button onClick={submitLeave} data-testid="leave-submit">Invia richiesta</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
